import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/common";
import { usePaged, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserCog, Plus, Pencil, KeyRound, Trash2, ShieldCheck, Store, Search } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Admin", cashier: "Kasir" };

export default function AdminStaff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", password: "", role: "admin", unit: "putra" });
  const [editForm, setEditForm] = useState({ name: "", phone: "", unit: "putra" });
  const [newPass, setNewPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addStaff = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Akun berhasil ditambahkan");
      setAddOpen(false);
      setForm({ name: "", phone: "", password: "", role: "admin", unit: "putra" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = (s) => { setEdit(s); setEditForm({ name: s.name, phone: s.phone, unit: s.unit || "putra" }); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/staff/${edit.id}`, editForm);
      toast.success("Akun diperbarui");
      setEdit(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${resetUser.id}/reset-password`, { new_password: newPass });
      toast.success(`Password ${resetUser.name} berhasil direset`);
      setResetUser(null); setNewPass("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Hapus akun ${s.name} (${s.phone})?`)) return;
    try {
      await api.delete(`/admin/staff/${s.id}`);
      toast.success("Akun dihapus");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = staff.filter((s) => {
    const q = query.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.phone || "").includes(query) ||
      ROLE_LABEL[s.role].toLowerCase().includes(q);
  });
  const pg = usePaged(filtered, 10);

  return (
    <div>
      <PageHeader
        title="Akun Petugas"
        subtitle="Kelola akun petugas (Admin & Kasir): ubah nama/nomor HP, tambah akun, reset password."
        action={
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-staff-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                <Plus size={18} className="mr-2" /> Tambah Akun
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Tambah Akun Petugas</DialogTitle></DialogHeader>
              <form onSubmit={addStaff} className="space-y-4 mt-1">
                <div className="space-y-2">
                  <Label>Nama</Label>
                  <Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Nomor HP</Label>
                  <Input data-testid="staff-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="rounded-xl" placeholder="0812xxxxxxxx" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input data-testid="staff-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="rounded-xl" placeholder="Minimal 6 karakter" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Peran</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="staff-role-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="cashier">Kasir</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.role === "cashier" && (
                    <div className="space-y-2">
                      <Label>Unit Kasir</Label>
                      <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                        <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="putra">Putra</SelectItem>
                          <SelectItem value="putri">Putri</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy} data-testid="save-staff-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">Simpan</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="relative mb-4 max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="staff-search" placeholder="Cari nama / nomor HP / peran…" className="pl-10 rounded-xl py-6 bg-white" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {staff.length === 0 ? (
          <Empty><UserCog className="mx-auto mb-2" /> Belum ada akun staf.</Empty>
        ) : filtered.length === 0 ? (
          <Empty><Search className="mx-auto mb-2" /> Tidak ada hasil untuk "{query}".</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nama</TableHead>
                <TableHead>Nomor HP</TableHead>
                <TableHead>Peran</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((s) => (
                <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                  <TableCell className="font-medium text-stone-800">
                    {s.name}{s.id === user?.id && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Anda</span>}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{s.phone}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${s.role === "admin" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>
                      {s.role === "admin" ? <ShieldCheck size={12} /> : <Store size={12} />}
                      {ROLE_LABEL[s.role]}{s.role === "cashier" ? ` ${s.unit || ""}` : ""}
                    </span>
                  </TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`} className="rounded-lg"><Pencil size={14} /></Button>
                    {s.id !== user?.id && (
                      <Button size="sm" variant="outline" onClick={() => { setResetUser(s); setNewPass(""); }} data-testid={`reset-staff-${s.id}`} className="rounded-lg"><KeyRound size={14} /></Button>
                    )}
                    {s.id !== user?.id && (
                      <Button size="sm" variant="outline" onClick={() => del(s)} data-testid={`delete-staff-${s.id}`} className="rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager {...pg} />
      </div>

      {/* Edit */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Edit Akun {edit?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-1">
            <div className="space-y-2">
              <Label>Nama</Label>
              <Input data-testid="edit-staff-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Nomor HP</Label>
              <Input data-testid="edit-staff-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="rounded-xl" />
            </div>
            {edit?.role === "cashier" && (
              <div className="space-y-2">
                <Label>Unit Kasir</Label>
                <Select value={editForm.unit} onValueChange={(v) => setEditForm({ ...editForm, unit: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="putra">Putra</SelectItem>
                    <SelectItem value="putri">Putri</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={busy} data-testid="save-edit-staff" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setNewPass(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Reset Password {resetUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-1">
            <Label>Password Baru</Label>
            <Input data-testid="reset-staff-password-input" type="text" value={newPass} onChange={(e) => setNewPass(e.target.value)} className="rounded-xl" placeholder="Minimal 6 karakter" />
          </div>
          <DialogFooter>
            <Button onClick={doReset} disabled={busy || newPass.length < 6} data-testid="confirm-reset-staff" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
